import React, { useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { format } from "date-fns";

const CATEGORY_LABELS = {
  "macro-note": "Macro Note", "weekly-outlook": "Weekly Outlook",
  "trade-idea": "Trade Idea", "education": "Education", "resource": "Resource",
};
const ROUTE_MAP = {
  "macro-note": "insights", "weekly-outlook": "weekly-outlook",
  "trade-idea": "trade-ideas", "education": "education", "resource": "education",
};
const CAT_COLORS = {
  "macro-note": "#818cf8", "weekly-outlook": "#f5c518",
  "trade-idea": "#4ade80", "education": "#f87171", "resource": "#c9a84c",
};

function formatDate(ts) {
  if (!ts) return "";
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return format(d, "dd MMM yyyy");
}

export default function PostCard({ post, featured = false }) {
  const route = ROUTE_MAP[post.category] || "insights";
  const href = `/${route}/${post.slug}`;
  const color = CAT_COLORS[post.category] || "#c9a84c";
  const ref = useRef(null);

  const onMove = useCallback((e) => {
    const el = ref.current; if (!el) return;
    const r = el.getBoundingClientRect();
    const dx = (e.clientX - r.left - r.width / 2) / (r.width / 2);
    const dy = (e.clientY - r.top - r.height / 2) / (r.height / 2);
    el.style.transform = `perspective(800px) rotateY(${dx * 6}deg) rotateX(${-dy * 6}deg) translateY(-5px)`;
    el.style.boxShadow = `${-dx * 10}px ${dy * 10}px 40px rgba(0,0,0,0.65), 0 0 30px ${color}12`;
    el.style.borderColor = `${color}45`;
    el.querySelector(".card-top-line").style.transform = "scaleX(1)";
  }, [color]);

  const onLeave = useCallback(() => {
    const el = ref.current; if (!el) return;
    el.style.transform = "perspective(800px) rotateY(0) rotateX(0) translateY(0)";
    el.style.boxShadow = "";
    el.style.borderColor = "#1e1e1e";
    el.querySelector(".card-top-line").style.transform = "scaleX(0)";
  }, []);

  return (
    <Link to={href} style={{ display: "block", textDecoration: "none", height: "100%" }}>
      <article
        ref={ref}
        onMouseMove={onMove}
        onMouseLeave={onLeave}
        style={{
          background: "#0a0a0a",
          border: "1px solid #1e1e1e",
          borderRadius: "10px",
          overflow: "hidden",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          position: "relative",
          transition: "transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease",
          transformStyle: "preserve-3d",
          willChange: "transform",
        }}
      >
        {/* Animated top colour line */}
        <div className="card-top-line" style={{ position: "absolute", top: 0, left: 0, right: 0, height: 2, background: `linear-gradient(90deg,${color},transparent)`, transform: "scaleX(0)", transformOrigin: "left", transition: "transform 0.35s ease", zIndex: 2 }} />

        {/* Corner glow */}
        <div style={{ position: "absolute", top: -30, right: -30, width: 140, height: 140, borderRadius: "50%", background: `radial-gradient(ellipse,${color}10 0%,transparent 70%)`, pointerEvents: "none", transition: "opacity 0.3s" }} />

        {/* Cover Image */}
        {post.coverImage && (
          <div style={{ height: featured ? 240 : 180, overflow: "hidden", position: "relative" }}>
            <img src={post.coverImage} alt={post.title} style={{ width: "100%", height: "100%", objectFit: "cover", transition: "transform 0.6s ease" }}
              onMouseEnter={e => e.target.style.transform = "scale(1.06)"}
              onMouseLeave={e => e.target.style.transform = "scale(1)"}
            />
            <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to top, #0a0a0a 0%, transparent 60%)" }} />
          </div>
        )}

        <div style={{ padding: featured ? "28px 28px 24px" : "22px 22px 18px", flex: 1, display: "flex", flexDirection: "column" }}>
          {/* Category badge */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
            <span style={{ fontFamily: "var(--font-mono)", fontSize: "9px", padding: "3px 10px", borderRadius: "20px", background: `${color}12`, border: `1px solid ${color}28`, color, letterSpacing: "0.14em", textTransform: "uppercase" }}>
              {CATEGORY_LABELS[post.category] || post.category}
            </span>
            {post.featured && <span style={{ fontFamily: "var(--font-mono)", fontSize: "9px", padding: "3px 8px", borderRadius: "20px", background: "#c9a84c10", border: "1px solid #c9a84c25", color: "#c9a84c", letterSpacing: "0.1em", textTransform: "uppercase" }}>Featured</span>}
          </div>

          {/* Title */}
          <h3 style={{ fontFamily: "var(--font-serif)", fontSize: featured ? "1.4rem" : "1.1rem", fontWeight: 500, color: "#ececec", marginBottom: 10, lineHeight: 1.3, transition: "color 0.2s" }}>
            {post.title}
          </h3>

          {/* Excerpt */}
          {post.excerpt && (
            <p style={{ fontSize: "13px", color: "#ffffff", lineHeight: 1.75, marginBottom: 16 }}>
              {post.excerpt.length > 110 ? post.excerpt.substring(0, 110) + "…" : post.excerpt}
            </p>
          )}

          {/* Footer */}
          <div style={{ marginTop: "auto", display: "flex", justifyContent: "space-between", alignItems: "center", paddingTop: 14, borderTop: "1px solid #141414" }}>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {post.tags?.slice(0, 2).map(tag => (
                <span key={tag} style={{ fontFamily: "var(--font-mono)", fontSize: "9px", color: color + "80", letterSpacing: "0.08em" }}>#{tag}</span>
              ))}
            </div>
            <span style={{ fontFamily: "var(--font-mono)", fontSize: "10px", color: "#ffffff", letterSpacing: "0.06em" }}>
              {formatDate(post.publishDate || post.createdAt)}
            </span>
          </div>
        </div>
      </article>
    </Link>
  );
}
